/**
 * Sound Discovery for Poker Reborn
 *
 * Auto-discovers and groups sound files by base name.
 *
 * Naming Convention:
 *   {base}{N}.mp3 → Sound ID: "{base}" with variants [base1, base2, ...]
 *   {base}.mp3    → Sound ID: "{base}" with single variant [base]
 *
 * Example:
 *   deal1.mp3, deal2.mp3, deal3.mp3 → Sound ID "deal" with 3 variants
 *   tick.mp3 → Sound ID "tick" with 1 variant
 *
 * @see docs/systems/SOUND_EVENTS.md - Sound system documentation
 */

import type { SoundEffect } from '@/types/ui';

// =============================================================================
// ALL SOUND FILES (from /assets/sounds/)
// =============================================================================

/**
 * Complete list of sound files available in /assets/sounds/.
 * Update this list when adding new sound files.
 */
export const ALL_SOUND_FILES: SoundEffect[] = [
  // Card sounds
  'deal1', 'deal2', 'deal3', 'deal4', 'deal5', 'deal6',
  'flop1', 'flop2', 'flop3', 'flop4',
  'turn1', 'turn2', 'turn3', 'turn4', 'turn5', 'turn6',
  'fold1', 'fold2', 'fold3',

  // Chip sounds
  'chip1', 'chip2', 'chip3', 'chip4',
  'chips_win',
  'chips_end',

  // Action sounds
  'check1', 'check2', 'check3',
  'knock1', 'knock2', 'knock3',
  'allin1', 'allin2', 'allin3',

  // Alert sounds
  'act', 'second_act',
  'tick',

  // UI sounds
  'button_click',
  'pop',
  'popup_open',
  'popup_close',

  // Celebration sounds
  'bonus',
  'applause',

  // Ambient sounds
  'room_noise',
  'music_groove',
];

// =============================================================================
// AUTO-DISCOVERY LOGIC
// =============================================================================

/**
 * Extract base name from a sound file ID.
 *
 * Examples:
 *   'deal1' → 'deal'
 *   'deal6' → 'deal'
 *   'chips_win' → 'chips_win'
 *   'act' → 'act'
 *   'second_act' → 'second_act' (no grouping, distinct sound)
 */
function extractBaseName(soundId: string): string {
  // Match trailing digits
  const match = soundId.match(/^(.+?)(\d+)$/);
  if (match) {
    return match[1] as string;
  }
  return soundId;
}

/**
 * Group sound files by their base name.
 *
 * @param soundFiles - Array of sound file IDs
 * @returns Record mapping base names to arrays of variants
 */
function groupSoundsByBaseName(
  soundFiles: readonly SoundEffect[]
): Record<string, SoundEffect[]> {
  const groups: Record<string, SoundEffect[]> = {};

  for (const soundId of soundFiles) {
    const baseName = extractBaseName(soundId);

    if (!groups[baseName]) {
      groups[baseName] = [];
    }
    groups[baseName].push(soundId);
  }

  // Sort variants within each group for consistency
  for (const baseName of Object.keys(groups)) {
    const variants = groups[baseName];
    if (variants) {
      variants.sort();
    }
  }

  return groups;
}

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Auto-discovered sound registry.
 *
 * Maps base names to arrays of variants:
 *   { deal: ['deal1', 'deal2', ...], flop: ['flop1', ...], tick: ['tick'], ... }
 *
 * Use base names as Sound IDs when playing sounds.
 */
export const SOUND_REGISTRY = groupSoundsByBaseName(ALL_SOUND_FILES);

/**
 * Get a random variant for a sound ID.
 *
 * @param soundId - Base sound ID (e.g., 'deal', 'flop')
 * @returns Random variant, or undefined if not found
 */
export function getRandomVariant(soundId: string): SoundEffect | undefined {
  const variants = SOUND_REGISTRY[soundId];
  if (!variants || variants.length === 0) {
    return undefined;
  }

  const index = Math.floor(Math.random() * variants.length);
  return variants[index];
}

/**
 * Check if a sound ID exists in the registry.
 */
export function hasSoundId(soundId: string): boolean {
  return soundId in SOUND_REGISTRY;
}

/**
 * Get all available sound IDs (base names).
 */
export function getAllSoundIds(): string[] {
  return Object.keys(SOUND_REGISTRY);
}
